export function companyCurrencyForm(company = {}) {
  return { reporting_currency: company.reporting_currency ?? '',
    head_office_country_code: company.head_office_country_code ?? '' };
}

export function companyCurrencyPayload(form) {
  const currency = String(form.reporting_currency ?? '').trim().toUpperCase();
  const country = String(form.head_office_country_code ?? '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) throw new Error('Select a company reporting currency.');
  if (country && !/^[A-Z]{2}$/.test(country)) throw new Error('Enter a two-letter head office country code.');
  return { reporting_currency: currency, head_office_country_code: country || null };
}

function id(value) {
  if (!/^[1-9][0-9]*$/.test(String(value)) || !Number.isSafeInteger(Number(value)) || Number(value) > 2147483647) {
    throw new Error('Select a valid company.');
  }
  return Number(value);
}
function companyFrom(data, expectedId) {
  const company = data?.company;
  if (!company || id(company.id) !== expectedId) throw new Error('The company response could not be verified.');
  return company;
}
function writeError(cause, submitted) {
  const status = cause?.response?.status;
  const uncertain = submitted || !status || status === 408 || status >= 500;
  return Object.assign(new Error(uncertain
    ? 'The company save could not be confirmed. Refresh to review the saved state before another change.'
    : 'The company change was rejected. Check the fields and your access.'), { uncertain });
}

// Resolve mount aliases using GET only. Never retry a company mutation.
export function createCompanySettingsRequests(client) {
  const paths = ['/admin/companies', '/admin/companies/companies', '/solo-mdsh-api/admin/companies'];
  async function resolve(companyId) {
    const target = companyId == null ? null : id(companyId);
    for (let index = 0; index < paths.length; index++) {
      const path = paths[index] + (target ? '/' + target : '');
      try {
        const { data } = await client.get(path);
        if (target) return { path, company: companyFrom(data, target) };
        const companies = Array.isArray(data) ? data : data?.companies;
        if (!Array.isArray(companies)) throw new Error('The company list could not be verified.');
        return { path, companies };
      } catch (error) {
        if (error?.response?.status === 404 && index < paths.length - 1) continue;
        throw error;
      }
    }
  }
  async function save(companyId, payload) {
    const target = companyId == null ? null : id(companyId);
    const settings = companyCurrencyPayload(payload);
    const body = target ? settings : { ...payload, ...settings };
    const resource = await resolve(target);
    let submitted = false;
    try {
      const { data } = target ? await client.patch(resource.path, body) : await client.post(resource.path, body);
      submitted = true;
      const savedId = target || id(data?.company?.id);
      const { data: stored } = await client.get(target ? resource.path : resource.path + '/' + savedId);
      const company = companyFrom(stored, savedId);
      for (const key of Object.keys(settings)) {
        if (!Object.hasOwn(company, key) || company[key] !== settings[key]) {
          throw new Error('Stored company settings do not match the submitted values.');
        }
      }
      if (!target && company.company_name !== body.company_name) throw new Error('The created company could not be confirmed.');
      return company;
    } catch (cause) { throw writeError(cause, submitted); }
  }
  return { get: async companyId => (await resolve(companyId)).company,
    create: payload => save(null, payload), update: (companyId, payload) => save(companyId, payload) };
}
